// /admin/v1/payments (contracts/web.md §5.7): the Stripe connection and settings, packages, transactions and
// webhook events. Optional fields default, "" reads as null and currencies are lower-cased, so a backend that
// sends a little less (or `livemode` instead of `mode`) still parses.

import { z } from "zod";
import { PAYMENT_MODES, WEB_PLANS, type PaymentMode } from "../lib/payments";

export const PaymentModeSchema = z.enum(PAYMENT_MODES);

/** A string that may be missing, null or ""; all read as null. */
const text = z
  .string()
  .nullish()
  .transform((v) => (v ? v : null));
/** A string that may be missing or null; reads as "". */
const blank = z
  .string()
  .nullish()
  .transform((v) => v ?? "");
const currency = z.string().transform((c) => c.toLowerCase());
const count = z.number().int().min(0);

/** `mode`, else `livemode`, else the mode the list was asked for (filled in by the caller). */
const withMode = <T extends { mode?: PaymentMode | undefined; livemode?: boolean | undefined }>(v: T) => ({
  ...v,
  mode: v.mode ?? (v.livemode === undefined ? undefined : v.livemode ? ("live" as const) : ("test" as const)),
});

// ---------------------------------------------------------------- connection and settings

export const StripeWebhook = z.object({
  id: text,
  url: text,
  /** Stripe's endpoint status: `enabled` or `disabled` (null when unknown). */
  status: text,
});
export type StripeWebhook = z.infer<typeof StripeWebhook>;

export const StripeConn = z.object({
  connected: z.boolean(),
  accountId: text,
  accountName: text,
  publishableKey: text,
  secretLast4: text,
  webhook: StripeWebhook.nullish().transform((w) => (w && (w.id || w.url) ? w : null)),
  connectedAt: text,
  lastEventAt: text,
});
export type StripeConn = z.infer<typeof StripeConn>;

const DISCONNECTED: z.input<typeof StripeConn> = { connected: false };

export const StripeSettings = z.object({
  mode: PaymentModeSchema,
  enabled: z.boolean(),
  allowPromotionCodes: z.boolean().default(true),
  automaticTax: z.boolean().default(false),
  trialOncePerUser: z.boolean().default(true),
  graceDays: count.default(7),
  test: StripeConn.default(StripeConn.parse(DISCONNECTED)),
  live: StripeConn.default(StripeConn.parse(DISCONNECTED)),
});
export type StripeSettings = z.infer<typeof StripeSettings>;

/** PATCH /payments/stripe (admin, audited). */
export interface StripeSettingsPatch {
  mode?: PaymentMode;
  enabled?: boolean;
  allowPromotionCodes?: boolean;
  automaticTax?: boolean;
  trialOncePerUser?: boolean;
  graceDays?: number;
}

// ---------------------------------------------------------------- packages

export const CurrencyOption = z.object({ currency, amount: count });
export type CurrencyOption = z.infer<typeof CurrencyOption>;

export const Package = z
  .object({
    id: z.string(),
    mode: PaymentModeSchema.optional(),
    livemode: z.boolean().optional(),
    plan: z.enum(WEB_PLANS),
    name: z.string(),
    description: blank,
    badge: blank,
    /** Minor units (599 = $5.99; 800 = ¥800). */
    amount: count,
    currency,
    currencyOptions: z.array(CurrencyOption).nullish().transform((v) => v ?? []),
    trialDays: count.nullish().transform((v) => v ?? 0),
    sort: z.number().int().nullish().transform((v) => v ?? 0),
    active: z.boolean(),
    archivedAt: text,
    stripeProductId: text,
    stripePriceId: text,
    stripeUrl: text,
    subscribers: count.nullish().transform((v) => v ?? null),
    createdAt: z.string(),
    updatedAt: text,
  })
  .transform(withMode);
export type Package = z.infer<typeof Package>;

export const Packages = z.object({ packages: z.array(Package) });
export type Packages = z.infer<typeof Packages>;

/** POST /payments/packages. */
export interface PackageInput {
  mode: PaymentMode;
  plan: (typeof WEB_PLANS)[number];
  name: string;
  description?: string;
  badge?: string;
  amount: number;
  currency: string;
  currencyOptions?: CurrencyOption[];
  trialDays?: number;
  sort?: number;
  active?: boolean;
}
/** PATCH /payments/packages/:id: any subset (the mode and plan stay). */
export type PackagePatch = Partial<Omit<PackageInput, "mode" | "plan">>;

// ---------------------------------------------------------------- transactions and events

export const Transaction = z.object({
  id: z.string(),
  userId: text,
  handle: text,
  plan: text,
  packageName: text,
  amount: z.number().int().nullish().transform((v) => v ?? null),
  currency: z
    .string()
    .nullish()
    .transform((v) => (v ? v.toLowerCase() : null)),
  /** Stripe's subscription status, or the panel's own words for a one-time payment; derived when absent. */
  status: text,
  inTrial: z.boolean().default(false),
  willRenew: z.boolean().default(false),
  expiresAt: text,
  revokedAt: text,
  createdAt: z.string(),
  stripeUrl: text,
});
export type Transaction = z.infer<typeof Transaction>;

export const Transactions = z.object({ items: z.array(Transaction), nextCursor: text });
export type Transactions = z.infer<typeof Transactions>;

export const WebhookEvent = z
  .object({
    id: z.string(),
    type: z.string(),
    mode: PaymentModeSchema.optional(),
    livemode: z.boolean().optional(),
    createdAt: z.string(),
    processedAt: text,
    /** How the server handled it: `processed`, `ignored`, `duplicate`, `orphan` or `error`; null while pending. */
    result: text,
    error: text,
    userId: text,
    handle: text,
    objectId: text,
    stripeUrl: text,
  })
  .transform(withMode);
export type WebhookEvent = z.infer<typeof WebhookEvent>;

/** `{ items, nextCursor }` (as transactions), or `{ events }`. */
export const WebhookEvents = z.union([
  z.object({ items: z.array(WebhookEvent), nextCursor: text }),
  z.object({ events: z.array(WebhookEvent), nextCursor: text }).transform(({ events, nextCursor }) => ({ items: events, nextCursor })),
]);
export type WebhookEvents = z.infer<typeof WebhookEvents>;
